import React, { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { FaSpinner, FaPaperPlane, FaExclamationCircle, FaUpload, FaTimes } from 'react-icons/fa';
import { submitContactForm, checkExistingContact } from '../../../services/contactService';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    file: null
  });
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [existingContact, setExistingContact] = useState(null);
  const [errors, setErrors] = useState({});
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const ALLOWED_FILE_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/jpeg',
    'image/png',
    'image/gif'
  ];

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    
    if (formData.file) {
      if (formData.file.size > MAX_FILE_SIZE) {
        newErrors.file = 'File size should be less than 5MB';
      }
      if (!ALLOWED_FILE_TYPES.includes(formData.file.type)) {
        newErrors.file = 'Invalid file type. Allowed types: PDF, DOC, DOCX, JPG, PNG, GIF';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkForExistingContact = async (email, message) => {
    try {
      setChecking(true);
      const response = await checkExistingContact(email, message);
      setExistingContact(response.existingContact);
      
      if (response.existingContact) {
        const { status, createdAt } = response.existingContact;
        const timeAgo = new Date(createdAt).toLocaleDateString();
        
        if (status === 'new' || status === 'in-progress') {
          toast.info(`You have a ${status} inquiry from ${timeAgo}. Our team will respond shortly.`);
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Error checking existing contact:', error);
      return false;
    } finally {
      setChecking(false);
    }
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Clear existing contact when email or message changes
    if (name === 'email' || name === 'message') {
      setExistingContact(null);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setErrors(prev => ({ ...prev, file: 'File size should be less than 5MB' }));
        return;
      }
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        setErrors(prev => ({ ...prev, file: 'Invalid file type. Allowed types: PDF, DOC, DOCX, JPG, PNG, GIF' }));
        return;
      }
      setFormData(prev => ({ ...prev, file }));
      setErrors(prev => ({ ...prev, file: '' }));
    }
  };

  const removeFile = () => {
    setFormData(prev => ({ ...prev, file: null }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    // Check for existing contact before submission
    const hasExisting = await checkForExistingContact(formData.email, formData.message);
    if (hasExisting) {
      toast.info('Please wait for our response to your existing inquiry before submitting a similar message.', {
        autoClose: 6000
      });
      return;
    }
    
    setLoading(true);
    try {
      const formDataToSend = new FormData();
      
      // Add all form fields
      formDataToSend.append('name', formData.name.trim());
      formDataToSend.append('email', formData.email.trim());
      formDataToSend.append('phone', formData.phone.trim());
      formDataToSend.append('subject', formData.subject.trim());
      formDataToSend.append('message', formData.message.trim());
      
      // Add file if present
      if (formData.file) {
        formDataToSend.append('file', formData.file);
      }

      // Log the form data being sent
      console.log('Sending form data:', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message,
        hasFile: !!formData.file
      });

      const response = await submitContactForm(formDataToSend);
      
      toast.success(response.message);
      const referenceNumber = response.contact.id.slice(-6).toUpperCase();
      toast.info(`Your reference number is #${referenceNumber}`, {
        autoClose: 8000
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        file: null
      });
      setExistingContact(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error(error.message || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputClasses = (error) => `
    mt-1 block w-full px-4 py-3
    rounded-lg
    border-2 
    ${error 
      ? 'border-red-500 dark:border-red-400' 
      : existingContact && existingContact.status !== 'closed'
        ? 'border-yellow-500 dark:border-yellow-400'
        : 'border-gray-300 dark:border-gray-600'
    }
    bg-white dark:bg-gray-800
    text-gray-900 dark:text-gray-100
    placeholder-gray-500 dark:placeholder-gray-400
    focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50
    focus:border-primary-500 dark:focus:border-primary-400
    transition duration-150 ease-in-out
    text-base
  `;

  const getStatusMessage = () => {
    if (!existingContact) return null;
    const { status, createdAt, _id } = existingContact;
    const timeAgo = new Date(createdAt).toLocaleDateString();
    const referenceNumber = _id.slice(-6).toUpperCase();
    
    if (status === 'new' || status === 'in-progress') {
      return (
        <div className="mb-6 space-y-4">
          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/50 border-l-4 border-yellow-500 rounded-r-lg">
            <div className="flex items-start space-x-3">
              <FaExclamationCircle className="h-5 w-5 text-yellow-500 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
              <div className="space-y-2">
                <p className="text-yellow-700 dark:text-yellow-200 font-medium">
                  Existing Inquiry In Progress
                </p>
                <p className="text-yellow-600 dark:text-yellow-300 text-sm">
                  You have an active inquiry from {timeAgo} (Ref: #{referenceNumber}) that is currently {status}.
                  Our team will respond to your previous message soon.
                </p>
                <div className="text-sm">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200">
                    Status: {status.charAt(0).toUpperCase() + status.slice(1)}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400 italic">
            To avoid duplicate inquiries, please wait for our response or submit a different message.
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto p-8 bg-white dark:bg-gray-800 rounded-xl shadow-lg">
      {getStatusMessage()}
      
      <div className="space-y-6">
        <div>
          <label htmlFor="name" className="block text-base font-medium text-gray-700 dark:text-gray-200 mb-2">
            Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Your full name"
            className={inputClasses(errors.name)}
          />
          {errors.name && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.name}</p>
          )}
        </div>

        <div>
          <label htmlFor="email" className="block text-base font-medium text-gray-700 dark:text-gray-200 mb-2">
            Email *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="your.email@example.com"
            className={inputClasses(errors.email)}
          />
          {errors.email && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.email}</p>
          )}
        </div>

        <div>
          <label htmlFor="phone" className="block text-base font-medium text-gray-700 dark:text-gray-200 mb-2">
            Phone (optional)
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="Your phone number"
            className={inputClasses()}
          />
        </div>

        <div>
          <label htmlFor="subject" className="block text-base font-medium text-gray-700 dark:text-gray-200 mb-2">
            Subject *
          </label>
          <input
            type="text"
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            placeholder="What is your message about?"
            className={inputClasses(errors.subject)}
          />
          {errors.subject && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.subject}</p>
          )}
        </div>

        <div>
          <label htmlFor="message" className="block text-base font-medium text-gray-700 dark:text-gray-200 mb-2">
            Message *
          </label>
          <textarea
            id="message"
            name="message"
            rows={6}
            value={formData.message}
            onChange={handleChange}
            placeholder="Type your message here..."
            className={inputClasses(errors.message)}
          />
          {errors.message && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="file" className="block text-base font-medium text-gray-700 dark:text-gray-200 mb-2">
            Attachment (optional)
          </label>
          <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-lg">
            <div className="space-y-1 text-center">
              {!formData.file ? (
                <>
                  <FaUpload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600 dark:text-gray-400">
                    <label
                      htmlFor="file-upload"
                      className="relative cursor-pointer bg-white dark:bg-gray-800 rounded-md font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
                    >
                      <span>Upload a file</span>
                      <input
                        id="file-upload"
                        name="file"
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        className="sr-only"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                      />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    PDF, DOC, DOCX, JPG, PNG, GIF up to 5MB
                  </p>
                </>
              ) : (
                <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-700 dark:text-gray-300">{formData.file.name}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      ({(formData.file.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={removeFile}
                    className="p-1 text-gray-500 hover:text-red-500 focus:outline-none"
                  >
                    <FaTimes className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
          {errors.file && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.file}</p>
          )}
        </div>

        <div>
          <button
            type="submit"
            disabled={loading || checking || (existingContact && (existingContact.status === 'new' || existingContact.status === 'in-progress'))}
            className="
              w-full flex justify-center items-center
              px-6 py-4 
              border border-transparent 
              rounded-lg
              text-base font-medium
              text-gray-900
              bg-primary-100 hover:bg-primary-200
              dark:bg-primary-500 dark:hover:bg-primary-600
              dark:text-white
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500
              disabled:opacity-50 disabled:cursor-not-allowed
              transform transition-transform duration-150 hover:scale-[1.02]
              shadow-lg hover:shadow-xl
            "
          >
            {loading || checking ? (
              <>
                <FaSpinner className="animate-spin mr-3 h-5 w-5 text-gray-900 dark:text-white" />
                <span>{loading ? 'Sending...' : 'Checking...'}</span>
              </>
            ) : (
              <>
                <FaPaperPlane className="mr-3 h-5 w-5 text-gray-900 dark:text-white" />
                <span>Send Message</span>
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

export default ContactForm; 